/**
 * The one ASIN reader for Amazon product addresses.
 *
 * Every path form Amazon uses for a product page: /dp/, /gp/product/,
 * /gp/aw/d/ (the old mobile site), /exec/obidos/asin/, /o/asin/, /product/,
 * and the ?asin= query some share links carry. It is the union of the three
 * readers that grew separately (pick-grounding.ts asinFromUrl,
 * present-product-core.ts amazonAsinOf, search-amazon-data.ts
 * extractASINFromAmazonURL); they can converge on this one.
 *
 * A leaf with no imports, so client code, the commerce-kit mirror and the
 * server can all use it.
 */

/** An ASIN: ten upper-case letters or digits (an ISBN-10 for books). */
export const ASIN_RE = /^[A-Z0-9]{10}$/;

const AMAZON_ASIN_PATH =
  /\/(?:dp|gp\/product|gp\/aw\/d|exec\/obidos\/asin|o\/asin|product)\/([A-Z0-9]{10})(?=\/|$)/i;

export function isAsin(value: unknown): value is string {
  return typeof value === "string" && ASIN_RE.test(value);
}

/** Where the ASIN sits in an Amazon product path, or null. */
export function amazonAsinPathMatch(pathname: string): { asin: string; index: number } | null {
  const match = AMAZON_ASIN_PATH.exec(pathname);
  return match ? { asin: match[1].toUpperCase(), index: match.index } : null;
}

/** The ASIN an Amazon product address names, upper case, or null. */
export function amazonAsinFromUrl(href: string | null | undefined): string | null {
  if (typeof href !== "string" || !href.trim()) return null;
  let url: URL;
  try {
    url = new URL(href.trim());
  } catch {
    return null;
  }
  const inPath = amazonAsinPathMatch(url.pathname);
  if (inPath) return inPath.asin;
  const query = (url.searchParams.get("asin") ?? url.searchParams.get("ASIN") ?? "").trim().toUpperCase();
  return ASIN_RE.test(query) ? query : null;
}
